import { useEffect } from 'react';

export interface ViewerAttachment {
  url: string;
  name: string | null;
  type: string | null;
}

type Kind = 'image' | 'video' | 'audio' | 'pdf' | 'other';

function extOf(name: string | null): string {
  return String(name || '').split('.').pop()?.toLowerCase() || '';
}

function kindOf(att: ViewerAttachment): Kind {
  const type = String(att.type || '').toLowerCase();
  const ext = extOf(att.name);
  if (type.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif', 'bmp', 'heic'].includes(ext)) return 'image';
  if (type.startsWith('video/') || ['mp4', 'webm', 'mov', 'm4v', 'ogv'].includes(ext)) return 'video';
  if (type.startsWith('audio/') || ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'webm'].includes(ext)) return 'audio';
  if (type === 'application/pdf' || ext === 'pdf') return 'pdf';
  return 'other';
}

/**
 * AttachmentViewer — ouvre une pièce jointe DANS Zifek (modale plein écran),
 * sans rediriger vers une nouvelle fenêtre. Images, vidéos, audio et PDF
 * sont affichés directement ; les autres formats proposent un téléchargement.
 */
export default function AttachmentViewer({
  attachment,
  onClose,
}: {
  attachment: ViewerAttachment;
  onClose: () => void;
}) {
  const kind = kindOf(attachment);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const label = attachment.name || 'Pièce jointe';

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-foreground-950/70" onClick={onClose}></div>

      <div className="relative w-full sm:max-w-4xl max-h-[92vh] bg-background-50 sm:rounded-2xl rounded-t-2xl flex flex-col overflow-hidden">
        <div className="flex items-center gap-3 px-4 h-14 border-b border-background-200/70 flex-shrink-0">
          <span className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-900 flex items-center justify-center flex-shrink-0">
            <i
              className={
                kind === 'image'
                  ? 'ri-image-line'
                  : kind === 'video'
                    ? 'ri-video-line'
                    : kind === 'audio'
                      ? 'ri-music-2-line'
                      : kind === 'pdf'
                        ? 'ri-file-pdf-2-line'
                        : 'ri-file-line'
              }
            ></i>
          </span>
          <p className="flex-1 min-w-0 text-sm font-medium text-foreground-900 truncate">{label}</p>
          <a
            href={attachment.url}
            download={attachment.name || undefined}
            className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-700 text-xs font-medium hover:bg-background-200/70 transition-colors no-underline whitespace-nowrap flex items-center gap-1.5"
            title="Télécharger"
          >
            <i className="ri-download-2-line"></i>
            <span className="hidden sm:inline">Télécharger</span>
          </a>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0"
            aria-label="Fermer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="flex-1 overflow-auto bg-background-100 flex items-center justify-center min-h-[220px]">
          {kind === 'image' && (
            <img src={attachment.url} alt={label} className="max-w-full max-h-[76vh] object-contain" />
          )}
          {kind === 'video' && (
            <video src={attachment.url} controls playsInline className="w-full max-h-[76vh] bg-black" />
          )}
          {kind === 'audio' && (
            <div className="w-full p-6">
              <audio src={attachment.url} controls className="w-full" />
            </div>
          )}
          {kind === 'pdf' && (
            <iframe src={attachment.url} title={label} className="w-full h-[76vh] bg-white" />
          )}
          {kind === 'other' && (
            <div className="p-8 text-center">
              <i className="ri-file-unknow-line text-4xl text-foreground-300"></i>
              <p className="mt-3 text-sm text-foreground-700">Aperçu indisponible pour ce format.</p>
              <a
                href={attachment.url}
                download={attachment.name || undefined}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold no-underline hover:bg-primary-600 transition-colors"
              >
                <i className="ri-download-2-line"></i>Télécharger le fichier
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}